/**
 * Layout-level provider for the Operations dock (collapsed by default).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/state/auth/AuthProvider';
import {
  OperationsPaneContext,
  isPlatformAdministratorRole,
} from './operationsContext';

export const OperationsPaneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const canUse = isPlatformAdministratorRole(user?.role);
  const [isOpen, setOpenState] = useState(false);
  const [mutationEpoch, setMutationEpoch] = useState(0);

  const notifyPageMutated = useCallback(() => {
    setMutationEpoch((n) => n + 1);
  }, []);

  const setOpen = useCallback(
    (open: boolean) => {
      if (!canUse) return;
      setOpenState(open);
    },
    [canUse]
  );

  const toggle = useCallback(() => setOpen(!isOpen), [isOpen, setOpen]);

  const value = useMemo(
    () => ({
      isOpen: canUse && isOpen,
      setOpen,
      toggle,
      canUse,
      mutationEpoch,
      notifyPageMutated,
    }),
    [canUse, isOpen, setOpen, toggle, mutationEpoch, notifyPageMutated]
  );

  return <OperationsPaneContext.Provider value={value}>{children}</OperationsPaneContext.Provider>;
};
